import { removeCookie } from "@insite/client-framework/Common/Cookies";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@tKgpco/theme/design";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    isEasylingIntegrationEnabled: state.context.website.properties.isEasylingIntegrationEnabled,
    pathName: getLocation(state).pathname,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    leftWrapper?: InjectableCss;
    rightWrapper?: InjectableCss;
    middleWrapper?: InjectableCss;
    contactWrapper?: InjectableCss;
    signInWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    scrollFixed?: InjectableCss;
    cartInWrapper?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
        css: css`
            padding: 0 !important;
            max-width: none !important;
            box-shadow: 0 0 4px 0 rgb(0 0 0 / 50%);
            .shipToHeader {
                background-color: rgb(241, 246, 251);
            }
            z-index: 10;
        `,
    },
    scrollFixed: {
        css: css`
            z-index: 1271;
            position: fixed;
            box-shadow: 0 0 4px 0 rgb(0 0 0 / 50%);
            top: 0;
            * {
                transition: all 0.4s ease;
            }
            @media print {
                position: relative !important;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-top: 0px;
                        max-width: 100%;
                        position: sticky !important;
                        top: 0 !important;
                        @media print {
                            position: relative !important;
                        }
                    `,
                    css`
                        padding-top: 0px;
                        max-width: 100%;
                        position: sticky !important;
                        top: 0 !important;
                        @media print {
                            position: relative !important;
                        }
                    `,
                    css`
                        padding-top: 0px;
                        max-width: 100%;
                        position: sticky !important;
                        top: 0 !important;
                        @media print {
                            position: relative !important;
                        }
                    `,
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                ]);
            }}
            & .secondaryNavigation, & .shipToHeader {
                display: none;
            }
            & .navigationLink {
                &:nth-child(1) {
                    display: block;
                }
            }
            & .headerLinksWrapper {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                position: absolute;
                                right: 10px;
                                top: 22px;
                            `,
                            css`
                                position: absolute;
                                right: 10px;
                                top: 22px;
                            `,
                            css`
                                position: absolute;
                                right: 10px;
                                top: 22px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
            & .megaMenuDrawerWrapper {
                top: 70px;
                @media (max-width: 1199px) and (min-width: 992px) {
                    top: 65px;
                }
            }
            & .customMenuDrawerWrapper {
                top: 70px;
                @media (max-width: 1199px) and (min-width: 992px) {
                    top: 65px;
                }
            }
        `,
    },
    container: {
        css: css`
            background-color: ${COLORS.white};
            display: flex;
            margin: 15px 20px 5px 20px;
            @media (max-width: 991px) and (min-width: 280px) {
                margin: 5px 20px 5px 20px;
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    leftWrapper: {
        css: css`
            flex-basis: 15%;
            /*@media (max-width: 1199px) and (min-width: 992px) {
                flex-basis: 10%;
            }*/
            img {
                width: auto;
                @media (max-width: 1199px) and (min-width: 992px) {
                    max-width: 130px;
                    width: auto;
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: 2;
                            flex-basis: calc(100% - 60px);
                            & > div {
                                display: flex;
                                justify-content: flex-start;
                                padding-left: 20px;
                            }
                            img {
                                height: 35px;
                                margin-top: 0.5rem;
                            }
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    middleWrapper: {
        css: css`
            flex-basis: 70%;
            /*@media (max-width: 1199px) and (min-width: 992px) {
                flex-basis: 54%;
            }*/
            > div {
                height: 100%;
                > div {
                    height: 100%;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: 1;
                            flex-basis: 20px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    rightWrapper: {
        css: css`
            flex-basis: 15%;
            /*@media (max-width: 1199px) and (min-width: 992px) {
                flex-basis: 35%;
            }*/
            display: flex;
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    contactWrapper: {
        css: css`
            margin-right: 12px;
        `,
    },
    signInWrapper: {
        css: css`
            margin-right: 12px;
        `,
    },
    searchWrapper: {
        css: css`
            margin-right: 12px;
        `,
    },
    cartInWrapper: {
        css: css`
            margin-top: 1px;
            /*@media not all and (min-resolution: 0.001dpcm) {
                @supports (-webkit-appearance: none) {
                    @media (max-width: 1199px) and (min-width: 992px) {
                        display: flex;
                        align-items: center;
                        a{
                            span:nth-child(2){
                                    margin-top: 0.6rem;
                                }
                            }          
                        }
                    }
                }
            }*/
        `,
    },
};

const styles = headerStyles;

const Header = ({
    isEasylingIntegrationEnabled,
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    pathName,
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (!pathName.includes("/MyAccount/Orders")) {
            removeCookie("OrderHistory-Params");
        }
    }, [pathName]);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    const [offset, setOffset] = useState(0);
    useEffect(() => {
        window.onscroll = () => {
            const check = window.parent.document.getElementById("siteIFrame");
            if (!check) {
                setOffset(window.pageYOffset);
            } else {
                setOffset(1);
            }
        };
    }, []);

    useEffect(() => {
        if (isEasylingIntegrationEnabled?.toLowerCase() === "true") {
            // eslint-disable-next-line no-use-before-define
            loadScript("https://app.easyling.com/client/zck6qfiw/0/stub.js?disableSelector=true");
        }
    });

    const loadScript = (url: string, callback?: { (): void }) => {
        const scriptExists = document.getElementById(id);
        if (!scriptExists) {
            const script: any = document.createElement("script");
            script.type = "text/javascript";
            script.src = url;
            id !== "" ? (script.id = id) : null;
            document.getElementsByTagName("head")[0].appendChild(script);
            if (script.readyState) {
                // only required for IE <9
                script.onreadystatechange = function () {
                    if (script.readyState === "loaded" || script.readyState === "complete") {
                        script.onreadystatechange = null;
                        if (callback) {
                            callback();
                        }
                    }
                };
            } else {
                script.onload = function () {
                    if (callback) {
                        callback();
                    }
                };
            }
        }
        if (scriptExists && callback) {
            callback();
        }
    };

    return (
        <>
            <Page
                fullWidth={[true, true, true, true, true]}
                {...styles.page}
                {...(offset > 40 ? { ...styles.scrollFixed } : "")}
                isHeader={true}
            >
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <Hidden above="md">
                        <StyledWrapper className="shipToHeader">
                            <Zone contentId={id} zoneName="ShipTo" />
                        </StyledWrapper>
                    </Hidden>
                    <StyledWrapper className="secondaryNavigation">
                        <Zone contentId={id} zoneName="SecondaryNavigation" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.container}>
                        <StyledWrapper {...styles.leftWrapper}>
                            <Zone contentId={id} zoneName="Logo" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.middleWrapper}>
                            <Zone contentId={id} zoneName="MainNavigation" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.rightWrapper}>
                            <StyledWrapper {...styles.searchWrapper}>
                                <Zone contentId={id} zoneName="Search" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.contactWrapper}>
                                <Zone contentId={id} zoneName="Contact" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.signInWrapper}>
                                <Zone contentId={id} zoneName="SignIn" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.cartInWrapper}>
                                <Zone contentId={id} zoneName="Cart" />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
